/**
 * CampaignDetailPage — tabbed campaign workspace: Overview (budget +
 * funnel goals + copy facts + guest trials + lifecycle/kill switch),
 * Placements (channel deploys),
 * and Creative Studio (AI generation + review + ad deploys). Live
 * metrics land with the dashboard phase.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useAdoptCampaign,
  useAdoptCampaignLifecycle,
  useAdoptDailyAdLimit,
  useAdoptKillSwitch,
  useAdoptPlacements,
  useAdoptSpendCaps,
  usePutAdoptFunnelGoals,
} from '../hooks/useAdvertisingOptimization';
import { ARCHE_PATH } from '../constants';
import { zeroSpendLimitName } from '../spend-limit-zero';
import { PlacementsSection } from '../components/PlacementsSection';
import { CreativeStudioSection } from '../components/CreativeStudioSection';
import { CopyGuidanceCard } from '../components/CopyGuidanceCard';
import { GuestTrialsCard } from '../components/GuestTrialsCard';
import type { AdoptFunnelGoalDto, AdoptFunnelGoalInput } from '@/lib/api';

interface Props {
  holderId: string;
}

const GOAL_TYPES = [
  'click',
  'landing_view',
  'signup',
  'activation',
  'purchase',
  'custom_event',
] as const;

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function FunnelGoalsEditor({
  holderId,
  campaignId,
  goals,
}: {
  holderId: string;
  campaignId: string;
  goals: AdoptFunnelGoalDto[];
}) {
  const putGoals = usePutAdoptFunnelGoals(holderId, campaignId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AdoptFunnelGoalInput[]>([]);

  const startEdit = (): void => {
    setDraft(
      goals.length > 0
        ? goals.map((g) => ({
            stageOrder: g.stageOrder,
            name: g.name,
            goalType: g.goalType,
            eventKey: g.eventKey,
            isPrimary: g.isPrimary,
          }))
        : [
            { stageOrder: 0, name: 'Ad click', goalType: 'click', eventKey: 'ad_click' },
            {
              stageOrder: 1,
              name: 'Signup',
              goalType: 'signup',
              eventKey: 'signup',
              isPrimary: true,
            },
          ],
    );
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    await putGoals.mutateAsync(draft);
    setEditing(false);
  };

  if (!editing) {
    return (
      <section className="rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-medium">Funnel goals</h2>
          <button onClick={startEdit} className="text-sm text-primary hover:underline">
            {goals.length > 0 ? 'Edit' : 'Define goals'}
          </button>
        </div>
        {goals.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No funnel goals yet — required before activation.
          </p>
        ) : (
          <ol className="flex flex-col gap-1">
            {goals.map((g) => (
              <li key={g.id} className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground w-5">{g.stageOrder + 1}.</span>
                <span className="font-medium">{g.name}</span>
                <span className="text-xs text-muted-foreground">({g.goalType})</span>
                {g.isPrimary && (
                  <span className="text-xs rounded bg-primary/10 text-primary px-1.5 py-0.5">
                    primary
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h2 className="font-medium">Edit funnel goals</h2>
      {draft.map((g, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground w-5">{i + 1}.</span>
          <input
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={g.name}
            onChange={(e) =>
              setDraft(draft.map((d, j) => (j === i ? { ...d, name: e.target.value } : d)))
            }
            placeholder="Stage name"
          />
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={g.goalType}
            onChange={(e) =>
              setDraft(
                draft.map((d, j) =>
                  j === i
                    ? { ...d, goalType: e.target.value as AdoptFunnelGoalInput['goalType'] }
                    : d,
                ),
              )
            }
          >
            {GOAL_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            className="w-36 rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={g.eventKey}
            onChange={(e) =>
              setDraft(draft.map((d, j) => (j === i ? { ...d, eventKey: e.target.value } : d)))
            }
            placeholder="event_key"
          />
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="radio"
              name="primary-goal"
              checked={!!g.isPrimary}
              onChange={() => setDraft(draft.map((d, j) => ({ ...d, isPrimary: j === i })))}
            />
            primary
          </label>
          <button
            onClick={() =>
              setDraft(draft.filter((_, j) => j !== i).map((d, j) => ({ ...d, stageOrder: j })))
            }
            className="text-sm text-rose-600"
            aria-label={`Remove stage ${i + 1}`}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <button
          onClick={() =>
            setDraft([
              ...draft,
              { stageOrder: draft.length, name: '', goalType: 'custom_event', eventKey: '' },
            ])
          }
          className="text-sm text-primary hover:underline"
        >
          + Add stage
        </button>
      </div>
      {putGoals.isError && (
        <div className="text-sm text-rose-600">
          Failed to save goals — check every stage has a name and event key.
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => void save()}
          disabled={putGoals.isPending || draft.some((d) => !d.name.trim() || !d.eventKey.trim())}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={() => setEditing(false)}
          className="px-3 py-1.5 rounded-md border border-border text-sm"
        >
          Cancel
        </button>
      </div>
    </section>
  );
}

const DETAIL_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'placements', label: 'Placements' },
  { key: 'creatives', label: 'Creative Studio' },
] as const;

type DetailTab = (typeof DETAIL_TABS)[number]['key'];

/** The campaign banner for each self-resuming spend-limit pause. */
const SPEND_CAP_BANNERS: ReadonlyArray<{
  reason: string;
  title: string;
  cause: string;
  period: 'day' | 'month';
  link: string;
}> = [
  {
    reason: 'monthly_cap_reached',
    title: 'Paused until next month — monthly cap reached.',
    cause: "This month's ad spend reached this workspace's monthly cap",
    period: 'month',
    link: 'Spend limit settings',
  },
  {
    reason: 'wallet_monthly_cap_reached',
    title: 'Paused until next month — wallet monthly cap reached.',
    cause: "This month's spend on the managed ad accounts reached the wallet's monthly cap",
    period: 'month',
    link: 'Spend limit settings',
  },
  {
    reason: 'daily_limit_reached',
    title: 'Paused until tomorrow — daily ad limit reached.',
    cause: "Today's ad spend reached this workspace's daily ad limit",
    period: 'day',
    link: 'Daily ad limit settings',
  },
  {
    reason: 'wallet_daily_cap_reached',
    title: 'Paused until tomorrow — wallet daily cap reached.',
    cause: "Today's spend on the managed ad accounts reached the wallet's daily cap",
    period: 'day',
    link: 'Spend limit settings',
  },
];

export function CampaignDetailPage({ holderId }: Props) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const detailQuery = useAdoptCampaign(holderId, campaignId);
  const lifecycle = useAdoptCampaignLifecycle(holderId, campaignId ?? '');
  const killSwitch = useAdoptKillSwitch(holderId, campaignId ?? '');
  const placementsQuery = useAdoptPlacements(holderId, campaignId);
  // A pause kept by a $0 limit never resumes by itself; the banner says so.
  const limitQuery = useAdoptDailyAdLimit(holderId);
  const capsQuery = useAdoptSpendCaps(holderId);
  const [tab, setTab] = useState<DetailTab>('overview');

  if (detailQuery.isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!detailQuery.data)
    return <div className="p-6 text-muted-foreground">Campaign not found.</div>;

  const { campaign, funnelGoals, trials } = detailQuery.data;
  // A stop (kill switch, pause/complete/archive) commits here first and then
  // pauses each placement on Google; any that did not land are retried by
  // the spend-sync sweep. Say so rather than imply the ads are off.
  const pendingChannelPauses = [
    ...(killSwitch.data?.channelPause ?? []),
    ...(lifecycle.data?.channelPause ?? []),
  ].filter((o) => !o.ok).length;
  const pausedPlacements = (placementsQuery.data ?? []).filter((p) => p.status === 'paused');
  // A spend limit a person set to $0 keeps these paused — one banner per
  // limit. Nothing comes back by itself afterwards: setting $0 relabels the
  // self-resuming pauses, so a person resumes what should run.
  const zeroBanners = new Map<string, number>();
  for (const p of pausedPlacements) {
    const name = zeroSpendLimitName(p.pauseReason, limitQuery.data, capsQuery.data);
    if (name) zeroBanners.set(name, (zeroBanners.get(name) ?? 0) + 1);
  }
  // The spend-sync sweep stopped these because a spend limit was reached;
  // it resumes them itself once the account's day (or month) has turned.
  const capBanners = SPEND_CAP_BANNERS.map((banner) => ({
    ...banner,
    count: pausedPlacements.filter(
      (p) =>
        p.pauseReason === banner.reason &&
        zeroSpendLimitName(p.pauseReason, limitQuery.data, capsQuery.data) === null,
    ).length,
  })).filter((b) => b.count > 0);
  const canActivate = campaign.status === 'draft' || campaign.status === 'paused';
  const canPause = campaign.status === 'active';
  // The activate route refuses a campaign with no funnel goal — an unmeasurable
  // campaign cannot be optimized. The wizard does not collect one, so a fresh
  // campaign's only header action used to be a button guaranteed to 422 with
  // nothing on screen explaining why. Say so, and point at the fix.
  const activateBlockedReason =
    funnelGoals.length === 0
      ? 'Add at least one funnel goal below before activating — the engine needs something to measure.'
      : campaign.killSwitch
        ? 'Release the kill switch before activating.'
        : null;

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{campaign.name}</h1>
          <p className="text-sm text-muted-foreground capitalize">
            {campaign.status} · {campaign.objective.toUpperCase()} ·{' '}
            {campaign.promotedArcheId ??
              (campaign.promotedTarget as { label?: string }).label ??
              ''}
          </p>
        </div>
        <div className="flex gap-2">
          {canActivate && (
            <button
              onClick={() => lifecycle.mutate('activate')}
              disabled={lifecycle.isPending || activateBlockedReason !== null}
              title={activateBlockedReason ?? undefined}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              Activate
            </button>
          )}
          {canPause && (
            <button
              onClick={() => lifecycle.mutate('pause')}
              disabled={lifecycle.isPending}
              className="px-3 py-1.5 rounded-md border border-border text-sm"
            >
              Pause
            </button>
          )}
          <button
            onClick={() => killSwitch.mutate(!campaign.killSwitch)}
            disabled={killSwitch.isPending}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              campaign.killSwitch
                ? 'border-border'
                : 'border-rose-300 text-rose-700 hover:bg-rose-50'
            }`}
          >
            {campaign.killSwitch ? 'Release kill switch' : 'Kill switch'}
          </button>
        </div>
      </header>

      {[...zeroBanners].map(([name, count]) => (
        <div
          key={`zero-${name}`}
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span className="font-medium">Paused — the {name} is $0.</span> No ads can run while it is
          $0, so {count === 1 ? 'its placement stays' : `${count} placements stay`} paused and
          nothing resumes by itself. Once a workspace owner or admin raises it, resume what should
          run from the Placements tab.{' '}
          <Link to={`${ARCHE_PATH}/settings`} className="underline">
            {name === 'daily ad limit' ? 'Daily ad limit settings' : 'Spend limit settings'}
          </Link>
        </div>
      ))}

      {capBanners.map((b) => (
        <div
          key={b.reason}
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span className="font-medium">{b.title}</span> {b.cause}, so{' '}
          {b.count === 1 ? 'its placement is' : `${b.count} placements are`} paused{' '}
          {b.period === 'day' ? 'for the rest of the day' : 'for the rest of the month'}. They
          resume automatically {b.period === 'day' ? 'on the next day' : 'next month'} in the ad
          account&apos;s time zone.{' '}
          <Link to={`${ARCHE_PATH}/settings`} className="underline">
            {b.link}
          </Link>
        </div>
      ))}

      {canActivate && activateBlockedReason && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {activateBlockedReason}
        </div>
      )}

      {pendingChannelPauses > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Stopped here, but {pendingChannelPauses} placement
          {pendingChannelPauses === 1 ? ' is' : 's are'} not yet paused on Google and may still be
          serving. Retrying automatically — see the Placements tab.
        </div>
      )}

      {lifecycle.isError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {lifecycle.error instanceof Error ? lifecycle.error.message : 'Action failed'}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-border">
        {DETAIL_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Total budget</div>
              <div className="text-xl font-semibold">{centsToUsd(campaign.totalBudgetCents)}</div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Daily ceiling</div>
              <div className="text-xl font-semibold">
                {centsToUsd(campaign.dailyBudgetCeilingCents)}
              </div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Autonomy</div>
              <div className="text-xl font-semibold capitalize">{campaign.autonomyLevel}</div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="text-sm text-muted-foreground">Objective target</div>
              <div className="text-xl font-semibold">
                {campaign.objectiveTargetValue !== null ? campaign.objectiveTargetValue : '—'}
              </div>
            </div>
          </div>

          <FunnelGoalsEditor holderId={holderId} campaignId={campaign.id} goals={funnelGoals} />

          <CopyGuidanceCard holderId={holderId} campaign={campaign} />

          {trials && <GuestTrialsCard holderId={holderId} campaign={campaign} trials={trials} />}
        </>
      )}

      {tab === 'placements' && (
        <PlacementsSection
          holderId={holderId}
          campaignId={campaign.id}
          dailyBudgetCeilingCents={campaign.dailyBudgetCeilingCents}
          campaignStatus={campaign.status}
          killSwitch={campaign.killSwitch}
        />
      )}

      {tab === 'creatives' && (
        <CreativeStudioSection
          holderId={holderId}
          campaignId={campaign.id}
          landingVariants={
            'landingVariants' in campaign.promotedTarget
              ? campaign.promotedTarget.landingVariants
              : undefined
          }
          bannedClaims={campaign.copyGuidance?.bannedClaims ?? []}
        />
      )}
    </div>
  );
}
